import type { CateringCatalog } from "@/features/caterings/types/catering-catalog.type";
import { GET } from "@/lib/methods";
import { fetchStrapi } from "@/services/strapi";
import type { NoResultsOfferType } from "../types/no-results-offer.type";

/*
Schema for generator: https://docs.strapi.io/dev-docs/api/rest/interactive-query-builder
{
  filters: {
    keywords: {
      $containsi: '[search]',
    },
  },
  pagination: {
    limit: 1,
  },
}
*/

/**
 * Looks up a "No results offer" entry from Strapi whose comma-separated
 * `keywords` string contains the searched phrase (case-insensitive).
 * Returns the first matching offer or null when nothing matches.
 */
export const getNoResultsOffer = async (search?: string) => {
  const phrase = search?.trim();

  if (!phrase) {
    return null;
  }

  const apiPath = `/no-results-offers?filters[keywords][$containsi]=${encodeURIComponent(
    phrase,
  )}&pagination[limit]=1`;

  const { data } = await fetchStrapi<NoResultsOfferType[]>(apiPath, {
    next: {
      tags: ["no-results-offer"],
      revalidate: 3600,
    },
  });

  return data?.[0] ?? null;
};

/**
 * Fetches catalog catering details for the offer, keyed by the catering id
 * stored in the CMS `catering` custom field. Returns null when the id is
 * empty or the catering can no longer be resolved (e.g. stale CMS reference),
 * so the caller can fall back to the generic no-results badge.
 */
export const getNoResultsOfferCatering = async (
  cateringId: string | null,
): Promise<CateringCatalog | null> => {
  if (!cateringId) {
    return null;
  }

  try {
    return await GET<CateringCatalog>({
      url: `${process.env.NEXT_PUBLIC_CATALOG_URL}/caterings/${cateringId}`,
      params: {
        next: {
          tags: ["catering", cateringId],
          revalidate: 3600,
        },
        headers: {
          "Content-Type": "application/json",
        },
      },
    });
  } catch {
    return null;
  }
};
